import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/shared";
import { api, errMsg, ago, label } from "@/lib/api";
import { ct } from "@/lib/time";

const Terms = ({ t, onDone }) => {
  const [ok, setOk] = useState(false);
  const accept = async () => { try { await api.post("/recruiter/placement-terms"); toast.success("Placement terms accepted"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel className="mb-6 border-amber-300 bg-amber-50" testid="placement-terms-panel">
      <h2 className="text-lg font-semibold">Placement terms — required before contacting candidates or posting jobs</h2>
      <p className="mt-2 text-sm text-slate-700">{t.text}</p>
      <label className="mt-4 flex items-center gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="placement-terms-checkbox" />I agree to pay ${t.fee.toLocaleString()} per hire under these terms</label>
      <Button className="mt-3 rounded-full" disabled={!ok} onClick={accept} data-testid="placement-terms-accept">Accept terms</Button>
    </Panel>
  );
};

const MarkDialog = ({ intro, onClose, onDone }) => {
  const [f, setF] = useState({ job_title: "", start_date: "" });
  const save = async () => {
    try { await api.post("/recruiter/placements", { intro_type: intro.intro_type, intro_id: intro.intro_id, ...f }); toast.success("Hire recorded"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!intro} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="placement-mark-dialog">
        <DialogHeader><DialogTitle>Record hire: {intro?.candidate_name}</DialogTitle></DialogHeader>
        <Input placeholder="Role they were hired for" value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} data-testid="placement-job-title" />
        <label className="text-sm font-medium">Start date (optional)<Input type="date" className="mt-1" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="placement-start-date" /></label>
        <Button disabled={f.job_title.length < 2} onClick={save} data-testid="placement-mark-submit">Record hire</Button>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ p, reload }) => {
  const [why, setWhy] = useState(null);
  const act = async (path, body) => { try { const r = await api.post(`/recruiter/placements/${p.placement_id}/${path}`, body || { origin_url: window.location.origin }); if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; } toast.success("Updated"); setWhy(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="border-b py-3 text-sm last:border-0" data-testid={`placement-row-${p.placement_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><p className="font-medium">{p.candidate_name} · {p.job_title}</p><p className="text-xs text-muted-foreground">{p.intro_label} · reported by {p.reported_by} · {ago(p.created_at)}{p.respond_by && p.status === "awaiting_recruiter" ? ` · respond by ${ct(p.respond_by)}` : ""}</p></div>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium" data-testid={`placement-status-${p.placement_id}`}>{label(p.status)}{p.fee_cents ? ` · $${(p.fee_cents / 100).toLocaleString()}` : ""}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {["awaiting_recruiter", "escalated"].includes(p.status) && <Button size="sm" onClick={() => act("confirm", {})} data-testid={`placement-confirm-${p.placement_id}`}>Confirm hire</Button>}
        {["awaiting_recruiter", "escalated", "due"].includes(p.status) && p.reported_by !== "recruiter" && <Button size="sm" variant="outline" onClick={() => setWhy("")} data-testid={`placement-dispute-${p.placement_id}`}>Dispute</Button>}
        {p.status === "due" && <Button size="sm" onClick={() => act("pay")} data-testid={`placement-pay-${p.placement_id}`}>Pay ${(p.fee_cents / 100).toLocaleString()}</Button>}
      </div>
      {why !== null && <div className="mt-2 space-y-2"><Textarea rows={2} placeholder="Why this isn't a hire through JobPedestal" value={why} onChange={(e) => setWhy(e.target.value)} data-testid="placement-dispute-reason" />
        <Button size="sm" disabled={why.length < 10} onClick={() => act("dispute", { reason: why })} data-testid="placement-dispute-submit">Send dispute</Button></div>}
    </div>
  );
};

export default function RecruiterPlacements({ onTerms }) {
  const [d, setD] = useState(null);
  const [mark, setMark] = useState(null);
  const load = () => api.get("/recruiter/placements").then((r) => { setD(r.data); onTerms?.(r.data.terms.accepted); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const intros = d.introductions.filter((i) => !i.hired_recorded);
  return (
    <>
      {!d.terms.accepted && <Terms t={d.terms} onDone={load} />}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Panel testid="placements-panel"><h2 className="mb-1 text-lg font-semibold">Hires & placement fees</h2>
          <p className="mb-3 text-xs text-muted-foreground">Flat ${d.terms.fee.toLocaleString()} per hire from a JobPedestal introduction. Candidate-reported hires can be confirmed or disputed within 14 days.</p>
          {d.placements.length === 0 ? <p className="text-sm text-muted-foreground">No hires recorded yet.</p> : d.placements.map((p) => <Row key={p.placement_id} p={p} reload={load} />)}
        </Panel>
        <Panel testid="introductions-panel"><h2 className="mb-3 text-lg font-semibold">Your JobPedestal introductions</h2>
          {intros.length === 0 ? <p className="text-sm text-muted-foreground">Accepted contacts and confirmed applicants appear here.</p> : intros.map((i) => (
            <div key={i.intro_id} className="flex items-center justify-between gap-2 border-b py-2 text-sm last:border-0">
              <span className="min-w-0 truncate">{i.candidate_name} · {i.label} · {ago(i.at)}</span>
              <Button size="sm" variant="outline" onClick={() => setMark(i)} data-testid={`intro-mark-hired-${i.intro_id}`}>Mark hired</Button></div>))}
        </Panel>
      </div>
      <MarkDialog intro={mark} onClose={() => setMark(null)} onDone={() => { setMark(null); load(); }} />
    </>
  );
}
